'use client';

import FilterDropdown from '@/components/blocks/FilterDropdown';
import StaticFocusFilter from '@/components/blocks/StaticFocusFilter';
import Image from '@/components/Image';
import { staticFilters } from '@/constants';
import { PathEnum } from '@/i18n/locale-config';
import { usePathname, useRouter } from '@/i18n/navigation';
import { cn } from '@/utils/tailwind-merge';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useCallback, useState } from 'react';

interface FiltersProps {
    data: {
        label: string;
        filters: Filter[];
        key: string;
        selectedFilters?: string[];
    }[];
    searchParams?: { [key: string]: string | string[] | undefined };
}

interface Filter {
    id: string;
    label: string;
}

const Filters = (props: FiltersProps) => {
    const { data } = props;
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const createQueryString = useCallback(
        (name: string, value: string) => {
            const params = new URLSearchParams(searchParams.toString());

            if (name === 'year' || name === 'screeningType') {
                params.set(name, value);
            } else {
                const existingValues = params.get(name)?.split(',') || [];

                if (!existingValues.includes(value)) {
                    existingValues.push(value);
                    params.set(name, existingValues.join(','));
                }
            }

            return params.toString();
        },
        [searchParams],
    );

    const onFilterChange = useCallback(
        (name: string, value: string) => {
            const pathKey = pathname as keyof typeof PathEnum;
            router.push(`${PathEnum[pathKey]}?${createQueryString(name, value)}`);
        },
        [createQueryString, pathname, router],
    );

    const onClearAll = useCallback(
        (name: string) => {
            const params = new URLSearchParams(searchParams.toString());
            params.delete(name);
            const pathKey = pathname as keyof typeof PathEnum;
            router.push(`${PathEnum[pathKey]}?${params.toString()}`);
        },
        [pathname, router, searchParams],
    );

    const DesktopFilters = () => (
        <div className="flex items-center gap-10 flex-wrap max-lg:hidden">
            {data.map((filter, index) => (
                <FilterDropdown
                    key={index}
                    label={filter.label}
                    filters={filter.filters}
                    initialSelectedFilters={filter.selectedFilters}
                    onFilterChange={(filterId) => onFilterChange(filter.key, filterId)}
                    onClearAll={() => onClearAll(filter.key)}
                />
            ))}
        </div>
    );

    const clearAllFilters = useCallback(() => {
        // @ts-ignore
        router.push(`${PathEnum[pathname]}`);
    }, [pathname, router]);

    // const saveFilters = useCallback(() => {
    //     const params = new URLSearchParams(searchParams.toString());
    //     // @ts-ignore
    //     router.push(`${PathEnum[pathname]}?${params.toString()}`);
    // }, [pathname, router, searchParams]);

    const MobileFilters = () => {
        const locale = useLocale() as 'en' | 'fr';
        const [isOpen, setIsOpen] = useState<boolean>(false);
        const selectedFiltersAmount = data.reduce(
            (acc, filter) => acc + (filter.selectedFilters?.length || 0),
            0,
        );

        // @ts-ignore
        const params = props.searchParams.focuses as string;
        const selectedFocuses = params ? params.split(',') : undefined;

        return (
            <>
                <button
                    className="text-left text-[25px] max-lg:flex hidden items-center gap-3"
                    onClick={() => setIsOpen(true)}
                >
                    <Image
                        className="inline-block"
                        src="/img/icons/filter.svg"
                        alt="Filter Icon"
                        width={18}
                        height={18}
                    />
                    FILTERS{' '}
                    <strong className="text-primary">
                        {selectedFiltersAmount > 0 ? <>({selectedFiltersAmount})</> : ''}
                    </strong>
                </button>
                <div
                    className={cn(
                        'flex-col gap-5 hidden p-5 fixed top-0 left-0 right-0 h-screen bg-black z-50',
                        isOpen && 'flex',
                    )}
                >
                    <div className="flex justify-between items-center">
                        <button
                            className="text-gray-200 leading-none uppercase text-[25px]"
                            onClick={clearAllFilters}
                        >
                            Clear all
                        </button>
                        {/* Commented out the save button for now */}
                        {/* <button
                            className="text-primary leading-none uppercase text-[25px]"
                            onClick={() => setIsOpen(false)}
                        >
                            Save
                        </button> */}
                    </div>
                    <div className="overflow-auto flex flex-col gap-5">
                        <h4 className="text-h4 border-b border-white/50">
                            Filters{' '}
                            {selectedFiltersAmount > 0 ? <>({selectedFiltersAmount})</> : ''}
                        </h4>
                        <div className="flex flex-col gap-5 pb-5 border-b border-white/50">
                            {staticFilters('filters').map((filter) => (
                                <StaticFocusFilter
                                    filter={filter}
                                    selectedFocuses={selectedFocuses || []}
                                    locale={locale}
                                    mobile
                                    currentPath={PathEnum.Films}
                                    key={filter.id}
                                />
                            ))}
                        </div>
                        {data.map((filter, index) => (
                            <FilterDropdown
                                key={index}
                                label={filter.label}
                                filters={filter.filters}
                                initialSelectedFilters={filter.selectedFilters}
                                onFilterChange={(filterId) => onFilterChange(filter.key, filterId)}
                                onClearAll={() => onClearAll(filter.key)}
                            />
                        ))}
                    </div>
                </div>
            </>
        );
    };

    return (
        <>
            <DesktopFilters />
            <MobileFilters />
        </>
    );
};

export default Filters;
